import {
  getLastDraftEditDate,
  getMappedDraftsByCardType,
  getMappedOrders,
} from 'cards/util/customization';
import _uniqBy from 'lodash/uniqBy';
import { ORDERED_CUSTOMIZATION_TYPES } from 'cards/constants/CardTypes';
import { ActionTypes, DraftsAndOrdersAction } from '../actions/draftsAndOrdersRedesignActions';

interface Drafts {
  [key: string]: any;
}

interface DraftsByMedium {
  [key: string]: Drafts;
}

interface DraftsAndOrdersRedesignState {
  activeDraftsTab: string;
  busy: boolean;
  waitingOnPreviewSuites: boolean;
  drafts: DraftsByMedium;
  orders: any[];
  published: any[];
  entities: {
    customizations: any[];
    projects: any[];
  };
}

const init = {
  activeDraftsTab: 'all',
  busy: false,
  waitingOnPreviewSuites: false,
  drafts: {
    PAPER: {
      INVITATION: [],
      RSVP: [],
      SAVE_THE_DATE: [],
      CHANGE_THE_DATE: [],
      ENCLOSURE: [],
      MENU: [],
      PROGRAM: [],
      PLACE: [],
      THANK_YOU: [],
      ENVELOPE: [],
      INVITATION_ENVELOPE: [],
      RSVP_ENVELOPE: [],
      HOLIDAY: [],
    },
    DIGITAL: {
      SAVE_THE_DATE: [],
    },
  },
  orders: [],
  published: [],
  entities: {
    customizations: [],
    projects: [],
  },
};

const reducer = (
  state: DraftsAndOrdersRedesignState = init,
  action: DraftsAndOrdersAction
): DraftsAndOrdersRedesignState => {
  switch (action.type) {
    case ActionTypes.DELETE_DRAFT: {
      const { projectUUID, medium, cardType } = action.payload;
      const { drafts } = state;
      const localMedium = medium === 'PAPER' || medium === 'MAGNET' ? 'PAPER' : 'DIGITAL';

      let newDrafts: any;

      const draftToDelete = drafts[localMedium][cardType].find(
        ({ project_uuid }: { project_uuid: string }) => project_uuid === projectUUID
      );

      if (draftToDelete.version_history && draftToDelete.version_history.length > 0) {
        const { version_history: versionHistory } = draftToDelete;
        const newCardTypeDrafts = drafts[localMedium][cardType]
          .concat(versionHistory)
          .filter(({ project_uuid }: { project_uuid: string }) => project_uuid !== projectUUID)
          .sort(
            // @ts-ignore
            (a: any, b: any) => getLastDraftEditDate(b) - getLastDraftEditDate(a)
          );

        const mappedNewCardTypeDrafts: any = [];

        newCardTypeDrafts.forEach((draft: any) => {
          const existingDraft = mappedNewCardTypeDrafts.find(
            ({ project_name }: { project_name: string }) => project_name === draft.project_name
          );

          if (!existingDraft) {
            mappedNewCardTypeDrafts.push(draft);
          } else {
            existingDraft.version_history.push(draft);
          }
        });

        newDrafts = mappedNewCardTypeDrafts;
      } else {
        newDrafts = drafts[localMedium][cardType].filter(
          ({ project_uuid }: { project_uuid: string }) => project_uuid !== projectUUID
        );
      }

      return {
        ...state,
        drafts: {
          ...state.drafts,
          [localMedium]: {
            ...state.drafts[localMedium],
            [cardType]: newDrafts,
          },
        },
      };
    }
    case ActionTypes.DELETE_PUBLISHED: {
      const { projectUUID } = action.payload;
      const { published } = state;

      const updatedPublished = published.filter(({ project_uuid }) => project_uuid !== projectUUID);

      return {
        ...state,
        published: updatedPublished,
      };
    }
    case ActionTypes.MOVE_PUBLISHED_TO_DRAFTS: {
      const { projectUUID } = action.payload;
      const { drafts, published } = state;

      const project = published.find(({ project_uuid }) => project_uuid === projectUUID);
      const updatedPublished = published.filter(({ project_uuid }) => project_uuid !== projectUUID);
      const updatedDrafts = drafts.DIGITAL.SAVE_THE_DATE.push(project);

      return {
        ...state,
        drafts: {
          ...state.drafts,
          ...updatedDrafts,
        },
        published: updatedPublished,
      };
    }
    case ActionTypes.REQUEST_PROJECTS:
      return {
        ...state,
        busy: true,
        waitingOnPreviewSuites: true,
      };
    case ActionTypes.RECEIVE_PROJECTS: {
      const {
        waitingOnPreviewSuites,
        drafts,
        orders,
        published,
        entities,
      } = action.payload.projects;
      const { PAPER = {}, MAGNET = {}, DIGITAL = {} } = drafts;

      // @ts-ignore
      const allPhysicalKeys = _uniqBy([...Object.keys(PAPER), ...Object.keys(MAGNET)]);

      const allPhysicalDrafts = allPhysicalKeys.reduce(
        (result: Record<string, any>, currentValue: string) => {
          let newContent: any[] = [];
          if (PAPER[currentValue] && Array.isArray(PAPER[currentValue])) {
            newContent = [...PAPER[currentValue]];
          }
          if (MAGNET[currentValue] && Array.isArray(MAGNET[currentValue])) {
            newContent = [...newContent, ...MAGNET[currentValue]];
          }
          // eslint-disable-next-line no-param-reassign
          result[currentValue] = newContent;
          return result;
        },
        {}
      );

      const mappedPaperDrafts = PAPER && getMappedDraftsByCardType(allPhysicalDrafts);
      const mappedDigitalDrafts = DIGITAL && getMappedDraftsByCardType(DIGITAL);
      const mappedOrders = orders.sort(
        (a: any, b: any) => (new Date(b.created_at) as any) - (new Date(a.created_at) as any)
      );
      const mappedPublished = getMappedOrders(published);

      return {
        ...state,
        busy: false,
        waitingOnPreviewSuites,
        drafts: {
          ...state.drafts,
          PAPER: {
            ...state.drafts.PAPER,
            ...mappedPaperDrafts,
          },
          DIGITAL: {
            ...state.drafts.DIGITAL,
            ...mappedDigitalDrafts,
          },
        },
        orders: mappedOrders,
        published: mappedPublished,
        entities,
      };
    }
    case ActionTypes.RECEIVE_CANCELLED_ORDER: {
      const { cancelledOrder } = action.payload;
      const { orders } = state;

      const updatedOrders = orders.map(order =>
        cancelledOrder.split_order_id === order.split_order_id ? cancelledOrder : order
      );

      return {
        ...state,
        orders: updatedOrders,
      };
    }
    case ActionTypes.SET_ACTIVE_DRAFTS_TAB: {
      const { key } = action.payload;

      return {
        ...state,
        activeDraftsTab: ORDERED_CUSTOMIZATION_TYPES.includes(key) ? key : 'all',
      };
    }
    case ActionTypes.TOGGLE_WAITING_ON_PREVIEW_SUITES: {
      const { waitingOnPreviewSuites } = action.payload;

      return {
        ...state,
        waitingOnPreviewSuites,
      };
    }
    default:
      return state;
  }
};

export default reducer;
